import { DataTable } from "@/components/data-table/data-table";
import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { DataTableToolbar } from "@/components/data-table/data-table-toolbar";
import {
  formatInterval,
  humanizeTimestamp,
  StatusIndicator,
} from "@/components/playlist/PlayListItem";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { useDataTable } from "@/hooks/use-data-table";
import { formatDate, formatLocationType } from "@/lib/format";
import { getPollingOneTimePlays, Play } from "@/queries/plays";
import type { ColumnDef } from "@tanstack/react-table";
import {
  Activity,
  ChevronRight,
  Clock,
  MapPin,
  Settings2,
  Tag,
  Target,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DuplicateAdHocPlayButton } from "./DuplicateAdHocPlayButton";

const getStatusOrder = (play: Play) => {
  if (play.completed_at) return 3;
  if (play.actioned_at) return 2;
  if (play.scheduled_at || play.override_scheduled_at) return 1;
  return 0;
};

const PlayListUpcoming = () => {
  const { data: oneTimePlaysData, isSuccess: oneTimeIsSuccess } =
    getPollingOneTimePlays({
      time_filter: "future",
    });
  const plays = [...(oneTimePlaysData?.plays || [])];

  const canRender = oneTimeIsSuccess && oneTimePlaysData?.plays;

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [locationFilter, setLocationFilter] = useState<string[]>([]);
  const [titleFilter, setTitleFilter] = useState<string>("");
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc";
  }>({
    key: "date",
    direction: "desc",
  });

  // Helper functions to manage URL parameters
  const getBooleanParam = (
    key: string,
    defaultValue: boolean = false
  ): boolean => {
    const value = searchParams.get(key);
    return value === null ? defaultValue : value === "true";
  };

  const setBooleanParam = (key: string, value: boolean) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, "true");
    } else {
      newParams.delete(key);
    }
    setSearchParams(newParams);
  };

  // URL-based filter states
  const showOneTime = getBooleanParam("showOneTime", true);

  const setShowOneTime = (value: boolean) =>
    setBooleanParam("showOneTime", value);

  // Handle row click to navigate to task detail
  const handleRowClick = (play: Play) => {
    navigate(`/plays/${play.id}`);
  };

  // Get current interval (time of day in 10-min intervals)
  const currentInterval = useMemo(() => {
    const now = new Date();
    return Math.floor((now.getHours() * 60 + now.getMinutes()) / 10);
  }, []);

  // Generate dynamic location options from actual data
  const locationOptions = useMemo(() => {
    const uniqueLocations = Array.from(
      new Set(plays.map((play) => play.location_type))
    ).sort();

    return uniqueLocations.map((location) => ({
      label: formatLocationType(location),
      value: location,
    }));
  }, [plays]);

  // Filter and sort the data manually
  const processedData = useMemo(() => {
    let filteredPlays = plays.slice();

    if (showOneTime) {
      filteredPlays = filteredPlays.filter((play) => play.adhoc);
    }

    // Apply location filter
    if (locationFilter.length > 0) {
      filteredPlays = filteredPlays.filter((play) =>
        locationFilter.includes(play.location_type)
      );
    }

    // Apply title filter
    if (titleFilter) {
      filteredPlays = filteredPlays.filter((play) =>
        play.title.toLowerCase().includes(titleFilter.toLowerCase())
      );
    }

    // Apply sorting
    filteredPlays.sort((a, b) => {
      let comparison = 0;

      switch (sortConfig.key) {
        case "date":
          const dateCompare =
            new Date(a.target_date).getTime() -
            new Date(b.target_date).getTime();

          if (dateCompare !== 0) {
            comparison = dateCompare;
          } else {
            comparison = a.start_interval - b.start_interval;
          }
          break;

        case "title":
          comparison = a.title.localeCompare(b.title);
          break;

        case "location_type":
        case "location":
          comparison = a.location_type.localeCompare(b.location_type);
          break;

        case "observed_threshold":
          // Put adhoc plays at the end
          if (a.adhoc && !b.adhoc) return 1;
          if (!a.adhoc && b.adhoc) return -1;
          if (a.adhoc && b.adhoc) return 0;

          // Sort by observed value for threshold plays
          const aObserved = a.observed_value || 0;
          const bObserved = b.observed_value || 0;
          comparison = aObserved - bObserved;
          break;

        case "status":
          // Custom status sorting: pending -> scheduled -> actioned -> completed
          comparison = getStatusOrder(a) - getStatusOrder(b);
          break;

        case "category":
          comparison = a.category.localeCompare(b.category);
          break;
      }

      return sortConfig.direction === "desc" ? -comparison : comparison;
    });

    return filteredPlays;
  }, [
    plays,
    locationFilter,
    titleFilter,
    sortConfig,
    showOneTime,
    currentInterval,
  ]);

  const columns = useMemo<ColumnDef<Play>[]>(
    () => [
      {
        id: "date",
        accessorKey: "date",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Date" />
        ),
        cell: ({ row }) => {
          const interval = row.original.start_interval;
          return (
            <div className="font-mono text-slate-300 opacity-80 flex flex-col gap-1">
              <div className="opacity-50 text-xs">
                {formatDate(row.original.target_date)}
              </div>
              <div className="flex items-center gap-2">
                <div>{formatInterval(interval)}</div>
                <div className="opacity-50 text-xs">
                  ({humanizeTimestamp(interval)})
                </div>
              </div>
            </div>
          );
        },
        meta: {
          label: "Time",
          icon: Clock,
        },
        enableSorting: true,
      },
      {
        id: "title",
        accessorKey: "title",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Title" />
        ),
        cell: ({ getValue }) => (
          <span className="font-semibold">{getValue() as string}</span>
        ),
        meta: {
          label: "Search",
          variant: "text",
        },
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: (row, columnId, filterValue) => {
          if (!filterValue || typeof filterValue !== "string") {
            return true;
          }
          const titleValue = row.getValue(columnId) as string;
          return titleValue.toLowerCase().includes(filterValue.toLowerCase());
        },
      },
      {
        id: "location",
        accessorKey: "location_type",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Location" />
        ),
        cell: ({ row }) => (
          <Badge variant="secondary">
            {row.original.location_type === "other"
              ? ""
              : formatLocationType(row.original.location_type)}
            {row.original.location_details &&
              ` ${row.original.location_details}`}
          </Badge>
        ),
        meta: {
          label: "Location",
          variant: "multiSelect",
          icon: MapPin,
          options: locationOptions,
        },
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: (row, columnId, filterValues) => {
          if (
            !filterValues ||
            !Array.isArray(filterValues) ||
            filterValues.length === 0
          ) {
            return true;
          }
          const locationValue = row.getValue(columnId) as string;
          return filterValues.includes(locationValue);
        },
      },
      {
        id: "observed_threshold",
        accessorFn: (row) =>
          !row.adhoc ? `${row.observed_value}/${row.threshold_value}` : "adhoc",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Observed/Threshold" />
        ),
        cell: ({ row }) => {
          if (row.original.adhoc) {
            return (
              <Badge
                variant="default"
                className="bg-green-700 hover:bg-green-700 text-white uppercase"
              >
                One-Time
              </Badge>
            );
          }
          return (
            <Badge variant="secondary" className="uppercase gap-1">
              <span>{row.original.observed_value} pax</span>
              <ChevronRight className="w-3 h-3" />
              <span>{row.original.threshold_value}</span>
            </Badge>
          );
        },
        meta: {
          label: "Observed/Threshold",
          icon: Target,
        },
        enableSorting: true,
        sortingFn: (rowA, rowB) => {
          const aAdhoc = rowA.original.adhoc;
          const bAdhoc = rowB.original.adhoc;

          // Put adhoc plays at the end
          if (aAdhoc && !bAdhoc) return 1;
          if (!aAdhoc && bAdhoc) return -1;
          if (aAdhoc && bAdhoc) return 0;

          // Sort by observed value for threshold plays
          const aObserved = rowA.original.observed_value || 0;
          const bObserved = rowB.original.observed_value || 0;
          return aObserved - bObserved;
        },
      },
      {
        id: "status",
        accessorFn: (row) => {
          if (row.completed_at) return "completed";
          if (row.actioned_at) return "actioned";
          if (row.scheduled_at || row.override_scheduled_at) return "scheduled";
          return "pending";
        },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Status" />
        ),
        cell: ({ row }) => (
          <div className="flex space-x-4">
            <StatusIndicator
              label="Schedule"
              timestamp={row.original.scheduled_at}
              override_timestamp={row.original.override_scheduled_at}
            />
            <StatusIndicator
              label="Execute"
              timestamp={row.original.actioned_at}
            />
            <StatusIndicator
              label="Complete"
              timestamp={row.original.completed_at}
            />
          </div>
        ),
        meta: {
          label: "Status",
          icon: Activity,
        },
        enableSorting: true,
        sortingFn: (rowA, rowB) => {
          // Custom status sorting: pending -> scheduled -> actioned -> completed
          const statusOrder = {
            pending: 0,
            scheduled: 1,
            actioned: 2,
            completed: 3,
          };
          const aStatus = rowA.getValue("status") as keyof typeof statusOrder;
          const bStatus = rowB.getValue("status") as keyof typeof statusOrder;
          return statusOrder[aStatus] - statusOrder[bStatus];
        },
      },
      {
        id: "category",
        accessorKey: "category",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Category" />
        ),
        cell: ({ getValue }) => (
          <Badge variant="outline" className="uppercase">
            {getValue() as string}
          </Badge>
        ),
        meta: {
          label: "Category",
          icon: Tag,
        },
        enableSorting: true,
      },
      {
        id: "actions",
        header: "",
        cell: ({ row }) => {
          const play = row.original;
          return (
            <div
              className="flex items-center gap-2"
              onClick={(e) => e.stopPropagation()}
            >
              <DuplicateAdHocPlayButton play={play} />
            </div>
          );
        },
        enableSorting: false,
      },
    ],
    [locationOptions]
  );

  const { table } = useDataTable({
    data: processedData,
    columns,
    pageCount: 1,
    onSortingChangeCallback: (sorting) => {
      if (sorting.length > 0) {
        const firstSort = sorting[0];
        setSortConfig({
          key: firstSort.id,
          direction: firstSort.desc ? "desc" : "asc",
        });
      }
    },
    onColumnFiltersChangeCallback: (columnFilters) => {
      const locationFilter = columnFilters.find(
        (filter) => filter.id === "location"
      );
      if (locationFilter && Array.isArray(locationFilter.value)) {
        setLocationFilter(locationFilter.value);
      } else if (!locationFilter) {
        setLocationFilter([]);
      }

      const titleFilter = columnFilters.find((filter) => filter.id === "title");
      if (titleFilter && typeof titleFilter.value === "string") {
        setTitleFilter(titleFilter.value);
      } else if (!titleFilter) {
        setTitleFilter("");
      }
    },
  });

  if (!canRender) return null;

  return (
    <DataTable
      table={table}
      hidePagination
      cellClassName="py-3"
      onRowClick={handleRowClick}
    >
      <DataTableToolbar table={table}>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-8 border-dashed">
              <Settings2 className="mr-2 h-4 w-4" />
              Display Options
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-0" align="start">
            <div className="p-4">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label
                    htmlFor="show-scheduled"
                    className="text-sm font-medium"
                  >
                    Show One-Time Only
                  </Label>
                  <Switch
                    id="show-one-time"
                    checked={showOneTime}
                    disabled={true}
                    onCheckedChange={setShowOneTime}
                  />
                </div>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </DataTableToolbar>
    </DataTable>
  );
};

export default PlayListUpcoming;
